import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Inventory2,
  Warning,
  CheckCircle,
  Add,
  Edit,
  Delete,
  Search,
  TrendingUp,
  Schedule
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';

const Inventory = () => {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [inventoryItems, setInventoryItems] = useState([]);

  const [newItem, setNewItem] = useState({
    meal: '',
    quantity: '',
    expiryDate: ''
  });

  const mealChoices = ['Beef', 'Chicken', 'Rice', 'Naan', 'Ice_Cream', 'Salad'];
  const mealCategories = ['All', 'Meat', 'Grains', 'Dairy', 'Vegetables'];

  // Load inventory data from backend
  const loadInventory = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/');
      setInventoryItems(response.data.inventory || []);
      setError('');
    } catch (err) {
      console.error('Error loading inventory:', err);
      setError('Failed to load inventory data');
      // Use food items as fallback
      setInventoryItems([
        { id: 1, meal: 'Beef', quantity: 25, expiryDate: '2024-02-15', timestamp: '2024-01-15T10:00:00Z' },
        { id: 2, meal: 'Chicken', quantity: 30, expiryDate: '2024-02-14', timestamp: '2024-01-14T10:00:00Z' },
        { id: 3, meal: 'Rice', quantity: 45, expiryDate: '2024-03-13', timestamp: '2024-01-13T10:00:00Z' },
        { id: 4, meal: 'Naan', quantity: 50, expiryDate: '2024-02-20', timestamp: '2024-01-15T10:00:00Z' },
        { id: 5, meal: 'Ice_Cream', quantity: 15, expiryDate: '2024-02-10', timestamp: '2024-01-12T10:00:00Z' },
        { id: 6, meal: 'Salad', quantity: 20, expiryDate: '2024-01-25', timestamp: '2024-01-14T10:00:00Z' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, []);

  const getStockStatus = (item) => {
    const expiryDate = new Date(item.expiryDate);
    const today = new Date();
    const daysToExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysToExpiry <= 3) return { status: 'Expiring Soon', color: 'error' };
    if (daysToExpiry <= 7) return { status: 'Check Soon', color: 'warning' };
    return { status: 'Fresh', color: 'success' };
  };

  const getCategoryFromMeal = (meal) => {
    const mealToCategory = {
      'Beef': 'Meat',
      'Chicken': 'Meat', 
      'Rice': 'Grains',
      'Naan': 'Grains',
      'Ice_Cream': 'Dairy',
      'Salad': 'Vegetables'
    };
    return mealToCategory[meal] || 'Other';
  };

  const handleAddItem = async () => {
    if (newItem.meal && newItem.quantity && newItem.expiryDate) {
      const item = {
        meal: newItem.meal,
        quantity: parseInt(newItem.quantity),
        expiryDate: newItem.expiryDate
      };

      try {
        setLoading(true);
        const response = await axios.post('https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/', item);
        await loadInventory(); // Reload data
        setNewItem({ meal: '', quantity: '', expiryDate: '' });
        setOpen(false);
        setError('');
      } catch (err) {
        console.error('Error adding item:', err);
        setError('Failed to add inventory item');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditItem = (item) => {
    setEditItem(item);
    setNewItem({
      meal: item.meal,
      quantity: item.quantity.toString(),
      expiryDate: item.expiryDate
    });
    setOpen(true);
  };

  const handleUpdateItem = async () => {
    if (!editItem) return;

    const updatedItem = {
      meal: newItem.meal,
      quantity: parseInt(newItem.quantity),
      expiryDate: newItem.expiryDate
    };

    try {
      setLoading(true);
      // Backend doesn't support PUT method for inventory updates
      // You would need to delete and recreate the item
      await axios.delete(`https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/?id=${editItem.id}`);
      await axios.post('https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/', updatedItem);
      await loadInventory(); // Reload data
      setEditItem(null);
      setNewItem({ meal: '', quantity: '', expiryDate: '' });
      setOpen(false);
      setError('');
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update inventory item');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/?id=${id}`);
      await loadInventory(); // Reload data
      setError('');
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete inventory item');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = item.meal.toLowerCase().includes(searchTerm.toLowerCase());
    const itemCategory = getCategoryFromMeal(item.meal);
    const matchesCategory = filterCategory === 'All' || itemCategory === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const expiringItems = inventoryItems.filter(item => {
    const expiryDate = new Date(item.expiryDate);
    const today = new Date();
    const daysToExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysToExpiry <= 7;
  });
  const totalItems = inventoryItems.length;
  const totalQuantity = inventoryItems.reduce((sum, item) => sum + item.quantity, 0);

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${color}15, ${color}05)` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box sx={{ bgcolor: color, borderRadius: 2, p: 1.5 }}>
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Inventory Management
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Track and manage your food stock levels
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpen(true)}
          sx={{
            background: 'linear-gradient(45deg, #667eea, #764ba2)',
            '&:hover': {
              background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
            }
          }}
        >
          Add Item
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Expiring Items Alert */}
      {expiringItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{expiringItems.length}</strong> items are expiring soon: {expiringItems.map(item => item.meal).join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Items"
            value={totalItems}
            icon={<Inventory2 sx={{ color: 'white' }} />}
            color="#667eea"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expiring Soon"
            value={expiringItems.length}
            icon={<Schedule sx={{ color: 'white' }} />}
            color="#f5576c"
            subtitle="Within 7 days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Quantity"
            value={totalQuantity}
            icon={<TrendingUp sx={{ color: 'white' }} />}
            color="#764ba2"
            subtitle="Total stock units"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Categories"
            value={mealCategories.length - 1}
            icon={<CheckCircle sx={{ color: 'white' }} />}
            color="#f093fb"
            subtitle="Food categories"
          />
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Filter by Category"
              >
                {mealCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Inventory Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
          Inventory Items ({filteredItems.length})
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Meal Item</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell align="center"><strong>Quantity</strong></TableCell>
                <TableCell align="center"><strong>Expiry Date</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Added</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => {
                const stockInfo = getStockStatus(item);
                const itemCategory = getCategoryFromMeal(item.meal);
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 'medium' }}>
                        {item.meal.replace('_', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={itemCategory} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {new Date(item.expiryDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={stockInfo.status}
                        color={stockInfo.color}
                        size="small"
                        icon={
                          stockInfo.status === 'Expiring Soon' ? <Warning /> :
                          stockInfo.status === 'Fresh' ? <CheckCircle /> : <Schedule />
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.timestamp).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditItem(item)}
                        sx={{ color: '#667eea' }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(item.id)}
                        sx={{ color: '#f5576c' }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Meal Item</InputLabel>
                <Select
                  value={newItem.meal}
                  onChange={(e) => setNewItem({ ...newItem, meal: e.target.value })}
                  label="Meal Item"
                >
                  {mealChoices.map((meal) => (
                    <MenuItem key={meal} value={meal}>
                      {meal.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={newItem.expiryDate}
                onChange={(e) => setNewItem({ ...newItem, expiryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
          <Button
            onClick={editItem ? handleUpdateItem : handleAddItem}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
              }
            }}
          >
            {loading ? 'Processing...' : (editItem ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory; 